import { DateRange, isNullOrUndefined }     from 'rewire-common';
import {
  Modal,
  Form,
  IFormContext,
  validator,
  field,
}                                           from 'rewire-ui';
import { IJob, IArea, IGroup, FieldFilter } from '../DataTypes';
import { getNameSearcher }                  from '../utility/Searchers';
import scheduleModel                        from './ScheduleModel';
import mainDateRangeNavModel                from '../ui/MainDateRangeNavModel';

export class DuplicateJobDialogModel extends Modal {
  form:      Form;
  jobId?:    string;
  group?:    IGroup;
  position?: number;

  constructor() {
    super('Duplicate Job');

    const groupFilter        = () => this.form.field.area.value ? {field: 'areaId', eq: this.form.field.area.value?.id} as FieldFilter : undefined;
    const areaSearcher       = getNameSearcher('areas');
    const groupSearcher      = getNameSearcher('groups', groupFilter);
    const occupationSearcher = getNameSearcher('occupations');
    const employeeSearcher   = getNameSearcher('employees');
    const generatorSearcher  = getNameSearcher('generators');

    this.form = Form.create((form: IFormContext) => ({
      numOfDuplicates:                    form.number({allowNegative: false, format: '#'}).label('# Duplicates').validators('required').autoFocus(),
      area:                               form.reference(areaSearcher).label('Area').validators('required').onValueChange(this.handleAreaChange),
      group:                              form.reference(groupSearcher).label('Group').validators('required'),
      occupation:                         form.reference(occupationSearcher).label('Occupation').validators('required'),
      autoCreateVacancyForAbsenceReasons: form.boolean().label('Auto Create Vacancy for Absence Reasons'),
      removeOverlappingAssignments:       form.boolean().label('Remove Overlapping Assignments'),
      effectiveStart:                     form.date().label('Effective Start Date').validators('required').endAdornment(() => null as any),
      effectiveEnd:                       form.date().label('Effective End Date').validators(validator('>=', field('effectiveStart'))).endAdornment(() => null as any),
      assignedEmployee:                   form.reference(employeeSearcher).label('Assigned Employee'),
      generator:                          form.reference(generatorSearcher).label('Shift Pattern'),
      cycleOffset:                        form.number({format: '###'}).label('Cycle Offset'),
    }));

    this.action('cancel')
        .action('save', this.submit, { variant: 'contained', type: 'submit' });
  }

  reset() {
    this.form.reset();
    this.jobId    = undefined;
    this.group    = undefined;
    this.position = undefined;
  }

  load(job: IJob) {
    this.reset();
    this.jobId      = job.id;
    this.group      = job.group;
    this.position   = job.position;
    this.form.value = {
      numOfDuplicates:                    1,
      area:                               this.group.area,
      group:                              this.group,
      occupation:                         job.occupation,
      autoCreateVacancyForAbsenceReasons: !!(job.fields?.['_autoCreateVacancyForAbsenceReasons:b']),
      removeOverlappingAssignments:       true,
      effectiveStart:                     mainDateRangeNavModel.startDateStartOfDay,
      assignedEmployee:                   undefined,
      generator:                          job.generator,
      cycleOffset:                        job.cycleOffset,
    };
    this.open();
  }

  handleAreaChange = (_form: Form, v?: IArea) => {
    if (!isNullOrUndefined(v)) {
      this.form.field.group.value = undefined;
    }
  };

  private save = async (): Promise<boolean> => {
    const area                               = this.form.field.area.value;
    const group                              = this.form.field.group.value;
    const occupation                         = this.form.field.occupation.value;
    const generator                          = this.form.field.generator.value;
    const employee                           = this.form.field.assignedEmployee.value;
    const cycleOffset                        = this.form.field.cycleOffset.value;
    const effective                          = new DateRange(this.form.field.effectiveStart.value, this.form.field.effectiveEnd.value);
    const autoCreateVacancyForAbsenceReasons = this.form.field.autoCreateVacancyForAbsenceReasons.value ?? true;
    const removeOverlappingAssignments       = this.form.field.removeOverlappingAssignments.value ?? true;

    const duplicateJobData: object = {
      area:        area,
      group:       group,
      occupation:  occupation,
      generator:   generator,
      employee:    employee,
      cycleOffset: cycleOffset,
      effective:   effective,
      position:    this.position,
      fields:      {'_autoCreateVacancyForAbsenceReasons:b': autoCreateVacancyForAbsenceReasons},
    };

    if (await scheduleModel.duplicateJob(duplicateJobData, this.form.field.numOfDuplicates.value, removeOverlappingAssignments, this.jobId)) {
      return true;
    }
    return false;
  };

  submit = async () => {
    return await this.form.submitAsync(this.save);
  };
}

const duplicateJobDialogModel = new DuplicateJobDialogModel();
export default duplicateJobDialogModel;
